import { useState, useEffect, useCallback, useContext } from "react";
import apiService from "../services/api.service";
import { useSession } from "../contexts/SessionContext";
import { AuthContext } from "../contexts/AuthContext";

// OPUS-1045: the org-scoped CTA ("Quick Links" / "My Apps") list plus the
// secure launch flow, extracted from LeftDrawer so the dedicated My Apps page
// reuses the EXACT same fetch + OPUS-901/902 server-minted handoff instead of
// duplicating the session-handoff logic. Re-fetches when the sidebar org
// changes or Settings bumps ctasVersion (add/edit/delete/reorder).
export function useQuickLinks() {
  const { selectedOrgId, ctasVersion } = useSession();
  const { userInfo } = useContext(AuthContext);
  const userOrg = userInfo?.data?.organization_id?._id;
  const org = selectedOrgId || userOrg;

  const [ctas, setCtas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!org) {
      setCtas([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    apiService
      .getSettings(org)
      .then((response) => {
        if (cancelled) return;
        const settingsData = response.data || response;
        const sorted = (settingsData.ctas || []).slice().sort((a, b) => a.seq - b.seq);
        setCtas(sorted);
      })
      .catch((error) => {
        if (!cancelled) console.error("Error fetching CTAs:", error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [org, ctasVersion]);

  // Pull user parameters from localStorage / refreshToken for CTA urlParams.
  const getUserParams = useCallback(() => {
    const params = {};
    try {
      const userDataStr = localStorage.getItem("userData");
      const refreshTokenStr = localStorage.getItem("refreshToken");

      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        const data = userData?.data || userData;
        if (data?._id) params.userId = String(data._id);
        if (data?.user_name) params.username = String(data.user_name);
        if (data?.organization_id) {
          if (typeof data.organization_id === "object" && data.organization_id._id) {
            params.orgId = String(data.organization_id._id);
          } else if (typeof data.organization_id === "string") {
            params.orgId = String(data.organization_id);
          }
        }
        if (data?.email) params.email = String(data.email);
        if (data?.role?.name) params.role = String(data.role.name);
      }

      if (refreshTokenStr && (!params.userId || !params.username)) {
        try {
          const tokenParts = refreshTokenStr.split(".");
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            if (payload.userId && !params.userId) params.userId = String(payload.userId);
            if (payload.username && !params.username) params.username = String(payload.username);
          }
        } catch (e) {
          console.warn("Could not decode refreshToken:", e);
        }
      }
    } catch (error) {
      console.error("Error getting user params:", error);
    }
    return params;
  }, []);

  const buildUrl = useCallback(
    (cta) => {
      let url = String(cta.href || "").trim();
      if (Array.isArray(cta.urlParams) && cta.urlParams.length > 0) {
        const userParams = getUserParams();
        const queryParams = new URLSearchParams();
        cta.urlParams.forEach((paramKey) => {
          if (userParams[paramKey]) queryParams.append(paramKey, String(userParams[paramKey]));
        });
        const queryString = queryParams.toString();
        if (queryString) url = `${url}${url.includes("?") ? "&" : "?"}${queryString}`;
      }
      if (!url.startsWith("http://") && !url.startsWith("https://")) url = `http://${url}`;
      return url;
    },
    [getUserParams],
  );

  // OPUS-901/902: open via a short-lived, server-minted handoff — no
  // session/refresh-token in the URL. Opens the tab synchronously on the user
  // gesture, then navigates once the handoff is minted (falling back to the
  // bare URL if minting fails).
  const launch = useCallback(
    async (cta) => {
      const baseUrl = buildUrl(cta);
      const targetOrg = cta.target_org_id || selectedOrgId || userOrg;
      const win = window.open("about:blank", "_blank");
      try {
        const resp = await apiService.mintHandoff(targetOrg);
        const handoff = resp?.data?.handoff;
        const finalUrl = handoff
          ? `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}h=${encodeURIComponent(handoff)}`
          : baseUrl;
        if (win) {
          win.opener = null;
          win.location.replace(finalUrl);
        } else {
          window.open(finalUrl, "_blank", "noopener,noreferrer");
        }
      } catch (e) {
        if (win) {
          win.opener = null;
          win.location.replace(baseUrl);
        }
      }
    },
    [buildUrl, selectedOrgId, userOrg],
  );

  return { ctas, loading, launch };
}

export default useQuickLinks;
